import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Tabs, TabList, TabPanels, TabPanel, useTab, useMultiStyleConfig, Flex, Box, Stack } from '@chakra-ui/react';
import { activeIcon, childTabStyles, disabledStyles, inactiveIcon, styles } from './secondary-navigation.styles';
import theme from '../../theme';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
export var SecondaryNavigationVariants = /*#__PURE__*/ function(SecondaryNavigationVariants) {
    SecondaryNavigationVariants["LINE"] = "line";
    SecondaryNavigationVariants["ENCLOSED"] = "enclosed";
    SecondaryNavigationVariants["ENCLOSED_COLORED"] = "enclosed-colored";
    SecondaryNavigationVariants["SOFT_ROUNDED"] = "soft-rounded";
    SecondaryNavigationVariants["SOLID_ROUNDED"] = "solid-rounded";
    SecondaryNavigationVariants["UNSTYLED"] = "unstyled";
    return SecondaryNavigationVariants;
}({});
export var SecondaryNavigationOrientation = /*#__PURE__*/ function(SecondaryNavigationOrientation) {
    SecondaryNavigationOrientation["HORIZONTAL"] = "horizontal";
    SecondaryNavigationOrientation["VERTICAL"] = "vertical";
    return SecondaryNavigationOrientation;
}({});
const SecondaryNavigation = ({ childTabIndex, data, handleNestedTabClick, handleTabsChange, activeTabColor = theme.colors.accent[200], orientation = "horizontal", tabColor = theme.colors.text['mediumEmphasis'], tabIndex = 0, variant = "line", ...rest })=>{
    const [_childTabIndex, setChildTabIndex] = useState(childTabIndex ?? 0);
    const handleChildTabsChange = (childTabIndex, parentLabel)=>{
        setChildTabIndex(childTabIndex);
        handleNestedTabClick?.(childTabIndex, parentLabel);
    };
    const onTabsChange = (tabIndex)=>{
        setChildTabIndex(0);
        handleTabsChange?.(tabIndex);
    };
    const CustomTab = (props)=>{
        const tabProps = useTab(props);
        const { label, disabled } = props.children;
        const tabStyles = useMultiStyleConfig('Tabs', tabProps);
        const classes = styles(tabStyles, orientation ?? "horizontal", activeTabColor, tabColor);
        const [isOpen, setIsOpen] = useState(label === props.parentTab);
        const handleChevronClick = ()=>{
            if (disabled) return;
            setIsOpen((prevState)=>!prevState);
        };
        const handleChildTabsChange = (e)=>{
            if (disabled) return;
            props.handleChildTabsChange(e, label);
        };
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs(Flex, {
                sx: Object.assign({}, classes, disabled && disabledStyles, props.parentTab === undefined ? childTabStyles.tabs : {}),
                ...tabProps,
                flexDirection: "column",
                alignItems: "baseline",
                "data-testid": `${label.trim()}tab`,
                "aria-disabled": disabled,
                children: [
                    /*#__PURE__*/ _jsxs(Flex, {
                        flexDirection: "row",
                        alignItems: "center",
                        onClick: handleChevronClick,
                        "data-testid": `${label.trim()}`,
                        children: [
                            /*#__PURE__*/ _jsx(Box, {
                                ml: orientation === "vertical" ? 0 : '2',
                                mr: "2",
                                children: label
                            }),
                            orientation === "vertical" && props.children?.children && /*#__PURE__*/ _jsx(_Fragment, {
                                children: isOpen ? /*#__PURE__*/ _jsx(BiomeIcon, {
                                    iconName: IconName.Chevron_Up,
                                    iconSize: "12",
                                    iconClass: `${label === props.parentTab ? activeIcon : inactiveIcon}`
                                }) : /*#__PURE__*/ _jsx(BiomeIcon, {
                                    iconName: IconName.Chevron_Down,
                                    iconSize: "12",
                                    iconClass: `${label === props.parentTab ? activeIcon : inactiveIcon}`
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Flex, {
                        children: isOpen && /*#__PURE__*/ _jsx(Tabs, {
                            index: props.childTabIndex,
                            onChange: handleChildTabsChange,
                            orientation: "vertical",
                            variant: "unstyled",
                            alignItems: "baseline",
                            children: /*#__PURE__*/ _jsx(Stack, {
                                spacing: theme.space[16],
                                alignContent: "center",
                                children: /*#__PURE__*/ _jsx(TabList, {
                                    children: props.children?.children?.map((childTab, index)=>/*#__PURE__*/ _jsx(CustomTab, {
                                            children: childTab
                                        }, `child-tab${index}`))
                                })
                            })
                        })
                    })
                ]
            })
        });
    };
    return /*#__PURE__*/ _jsxs(Tabs, {
        index: tabIndex ?? 0,
        onChange: onTabsChange,
        orientation: orientation,
        variant: variant,
        alignItems: "baseline",
        children: [
            /*#__PURE__*/ _jsx(TabList, {
                gap: orientation === "horizontal" ? '8px' : 0,
                children: data.map((tab, index)=>/*#__PURE__*/ _jsx(CustomTab, {
                        id: index,
                        handleNestedTabClick: handleNestedTabClick,
                        parentTab: tabIndex === index ? tab.label : '',
                        childTabIndex: _childTabIndex,
                        handleChildTabsChange: handleChildTabsChange,
                        children: tab
                    }, tab.label || index))
            }),
            /*#__PURE__*/ _jsx(TabPanels, {
                children: data.map((tab)=>/*#__PURE__*/ _jsx(TabPanel, {
                        p: 4,
                        children: tab.content
                    }, tab.label))
            })
        ]
    });
};
SecondaryNavigation.displayName = 'SecondaryNavigation';
export { SecondaryNavigation };
